// Local notifications wrapper. On iOS (Capacitor native) uses
// @capacitor/local-notifications. On web it no-ops gracefully.
//
// The streak-save reminder fires at 20:00 local time and reminds users
// whose current streak > 0 to open the app before midnight. We schedule
// one notification per day, ID 1000, and we replace it each time the
// streak is updated so it always reflects the latest count.

import { Capacitor } from "@capacitor/core";

const STREAK_NOTIF_ID = 1000;
const STREAK_HOUR = 20; // 8pm local

const CHECKIN_NOTIF_ID = 1001;
const CHECKIN_HOUR = 18; // 6pm local
const CHECKIN_WEEKDAY = 7; // Sunday

const PREF_KEY = "fff_notifications_enabled";

const isNative = () => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

let plugin = null;
const load = async () => {
  if (plugin) return plugin;
  const mod = await import("@capacitor/local-notifications");
  plugin = mod.LocalNotifications;
  return plugin;
};

export const notificationsEnabled = () => localStorage.getItem(PREF_KEY) === "1";

export const setNotificationsEnabled = (v) => {
  localStorage.setItem(PREF_KEY, v ? "1" : "0");
};

export const requestPermission = async () => {
  if (!isNative()) {
    setNotificationsEnabled(true); // web preview — treat as enabled
    return "granted";
  }
  try {
    const LN = await load();
    const res = await LN.requestPermissions();
    const granted = res?.display === "granted";
    setNotificationsEnabled(granted);
    return res?.display || "denied";
  } catch (e) {
    console.warn("requestPermission failed:", e);
    return "denied";
  }
};

const nextLocal8pm = () => {
  const d = new Date();
  d.setHours(STREAK_HOUR, 0, 0, 0);
  if (d.getTime() <= Date.now()) d.setDate(d.getDate() + 1);
  return d;
};

export const scheduleStreakReminder = async (streak) => {
  if (!isNative() || !notificationsEnabled()) return;
  if (!streak || streak < 1) return;
  try {
    const LN = await load();
    // Replace any prior reminder so the count stays current
    try { await LN.cancel({ notifications: [{ id: STREAK_NOTIF_ID }] }); } catch {}
    await LN.schedule({
      notifications: [{
        id: STREAK_NOTIF_ID,
        title: streak === 1 ? "Keep the spark alive" : `Your ${streak}-day streak`,
        body: streak === 1
          ? "Open FreeFitFuel before midnight to start your streak."
          : `Open FreeFitFuel before midnight to keep your ${streak}-day flame burning.`,
        schedule: { at: nextLocal8pm(), repeats: false, allowWhileIdle: true },
        smallIcon: "ic_stat_icon_config_sample",
      }],
    });
  } catch (e) {
    console.warn("scheduleStreakReminder failed:", e);
  }
};

export const cancelStreakReminder = async () => {
  if (!isNative()) return;
  try {
    const LN = await load();
    await LN.cancel({ notifications: [{ id: STREAK_NOTIF_ID }] });
  } catch {}
};

const nextWeeklyCheckin = () => {
  const d = new Date();
  d.setHours(CHECKIN_HOUR, 0, 0, 0);
  // JS getDay: Sun=0 .. Sat=6 — convert to ISO 1..7 (Mon=1, Sun=7)
  const todayIso = d.getDay() === 0 ? 7 : d.getDay();
  let diff = CHECKIN_WEEKDAY - todayIso;
  if (diff < 0 || (diff === 0 && d.getTime() <= Date.now())) diff += 7;
  d.setDate(d.getDate() + diff);
  return d;
};

export const scheduleCheckinReminder = async () => {
  if (!isNative() || !notificationsEnabled()) return;
  try {
    const LN = await load();
    try { await LN.cancel({ notifications: [{ id: CHECKIN_NOTIF_ID }] }); } catch {}
    await LN.schedule({
      notifications: [{
        id: CHECKIN_NOTIF_ID,
        title: "Weekly check-in",
        body: "Take 30 seconds to log this week — weight, energy, recovery, adherence.",
        schedule: { at: nextWeeklyCheckin(), repeats: true, every: "week", allowWhileIdle: true },
        smallIcon: "ic_stat_icon_config_sample",
      }],
    });
  } catch (e) {
    console.warn("scheduleCheckinReminder failed:", e);
  }
};

export const cancelCheckinReminder = async () => {
  if (!isNative()) return;
  try {
    const LN = await load();
    await LN.cancel({ notifications: [{ id: CHECKIN_NOTIF_ID }] });
  } catch {}
};
